import { axios, AxiosResponse } from "@sdk/axios";
import { SDKConfig } from "@sdk/default-sdk-config";
import { EntityServices } from "@sdk/utils/entity.service";
import { iBrand } from "./brand-model";

export class BrandServices {
  configure(config: { basePath?: string; token?: string }) {
    Object.assign(this.config, config);
  }
  constructor(protected config: { basePath: string; token: string }) {
    this.brands.configure(config);
  }

  brands = Object.assign(
    new EntityServices<iBrand>(SDKConfig, "brands", {}),
    {
      resolvePublicData: async (req: {
        brandId?: string;
        organizationId?: string;
        domain?: string;
      }): Promise<{
        brandId: string;
        organizationId: string;
        workspaceDomain?: string;
        pagesDomain?: string;
        logo?: string;
        primaryColor?: string;
        website?: string;
        name?: string;
      }> => {
        const results = await axios.post(
          this.config.basePath + `/brands/resolve-public-data`,
          req,
          {
            headers: {},
          }
        );
        return results.data;
      },
      setPagesCustomDomain: async (
        id: string,
        req: { domain: string }
      ): Promise<iBrand> => {
        const results: AxiosResponse<iBrand> = await axios.post(
          this.config.basePath + `/brands/${id}/pages-custom-domain`,
          req,
          {
            headers: {
              Authorization: this.config.token,
            },
          }
        );
        return results.data;
      },
      removePagesCustomDomain: async (id: string): Promise<iBrand> => {
        const results: AxiosResponse<iBrand> = await axios.delete(
          this.config.basePath + `/brands/${id}/pages-custom-domain`,
          {
            headers: {
              Authorization: this.config.token,
            },
          }
        );
        return results.data;
      },
      verifyPagesCustomDomain: async (id: string): Promise<iBrand> => {
        const results: AxiosResponse<iBrand> = await axios.post(
          this.config.basePath + `/brands/${id}/pages-custom-domain/verify`,
          {},
          {
            headers: {
              Authorization: this.config.token,
            },
          }
        );
        return results.data;
      },
      migrateAddBrandData: async (): Promise<any> => {
        const results = await axios.post(
          this.config.basePath + `/brands/migrate/add-brand-data`,
          {},
          {
            headers: {
              Authorization: this.config.token,
            },
          }
        );
        return results.data;
      },
      provisionDefaultLedgers: async (req?: {
        brandId?: string;
      }): Promise<
        Array<{
          organizationId: string;
          brandId: string;
          brandName: string;
          dryRun: boolean;
          status: string;
          defaultLedgerAccounts?: {
            accountsReceivableLedgerId: string;
            revenueLedgerId: string;
          };
          error?: string;
        }>
      > => {
        const results = await axios.post(
          this.config.basePath + `/brands/provision-default-ledgers`,
          req ?? {},
          {
            headers: {
              Authorization: this.config.token,
            },
          }
        );
        return results.data;
      },
      getOnboardingChecklist: async (
        id: string
      ): Promise<{
        items: Array<{
          id: string;
          label: string;
          description: string;
          category: "configuration" | "review";
          isComplete: boolean;
          isReviewed?: boolean;
          canPreview?: boolean;
          previewTemplateType?: string;
          relatedSection?: string;
        }>;
        completedCount: number;
        totalCount: number;
      }> => {
        const results = await axios.get(
          this.config.basePath + `/brands/${id}/onboarding-checklist`,
          {
            headers: {
              Authorization: this.config.token,
            },
          }
        );
        return results.data;
      },
      updateOnboardingChecklistItem: async (
        id: string,
        itemId: string,
        body: { isReviewed: boolean }
      ): Promise<iBrand> => {
        const results: AxiosResponse<iBrand> = await axios.patch(
          this.config.basePath +
            `/brands/${id}/onboarding-checklist/${itemId}`,
          body,
          {
            headers: {
              Authorization: this.config.token,
            },
          }
        );
        return results.data;
      },
      getEmailTemplatePreview: async (
        id: string,
        templateType:
          | "booking-confirmation"
          | "booking-confirmation-amended"
          | "booking-confirmation-voluntary-cancellation"
          | "booking-confirmation-involuntary-cancellation"
          | "booking-confirmation-refund"
          | "booking-confirmation-passenger-change"
          | "payment-reminder"
          | "voucher"
          | "customer-receipt"
      ): Promise<{ subjectline: string; body: string }> => {
        const results = await axios.get(
          this.config.basePath +
            `/brands/${id}/email-template-preview?templateType=${encodeURIComponent(templateType)}`,
          {
            headers: {
              Authorization: this.config.token,
            },
          }
        );
        return results.data;
      },
      sendTestEmail: async (
        brandId: string,
        payload: {
          to: string;
          templateType:
            | "booking-confirmation"
            | "booking-confirmation-amended"
            | "booking-confirmation-voluntary-cancellation"
            | "booking-confirmation-involuntary-cancellation"
            | "booking-confirmation-refund"
            | "booking-confirmation-passenger-change"
            | "payment-reminder"
            | "voucher"
            | "customer-receipt";
        }
      ): Promise<{ success: boolean; resendEmailId?: string }> => {
        const results = await axios.post(
          this.config.basePath + `/brands/${brandId}/send-test-email`,
          payload,
          {
            headers: {
              Authorization: this.config.token,
            },
          }
        );
        return results.data;
      },
    }
  );
}
